import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { Icon } from '../../../shared/ui/Primitives';
import type { ScenarioEntry, ScenarioProjection } from '../domain/types';
import { ScenarioCapabilityReadingCard } from './ScenarioCapabilityReading';
import { highriseCapabilityReading } from './scenario-capability-editorial';
import { ScenarioCapabilityDrawer } from './ScenarioCapabilityDrawer';
import { ScenarioEvidenceDrawer } from './ScenarioEvidenceDrawer';
import {
  evidenceGroupOf,
  evidenceGroupOrder,
  findIssue,
  issueBusinessTitles,
  issueContent,
  officialCases,
  officialDocuments,
  scenarioCards,
  type EvidenceGroup,
  type OfficialCase,
  type OfficialDocument,
} from './scenario-center-content';
import {
  entryLabel,
  entryStudyRelation,
  linkedEntries,
  type LinkedResult,
} from './scenario-research-reference';
import { ScenarioCardArt } from './ScenarioCardArt';
import { ScenarioTechnicalReference } from './ScenarioTechnicalReference';
import { informationAcquisition } from './scenario-reading-details';
import { handleScenarioTabsKeyDown } from './scenario-tab-keyboard';

export type DetailTab = 'issues' | 'evidence';
/** Which reading section the URL asked to land on. Never a second navigation. */
export type DetailFocus = 'information' | 'capability' | null;

export interface DetailLocation {
  readonly issue: string;
  readonly tab: DetailTab;
  readonly focus: DetailFocus;
}

const issueCodes = issueContent.map((issue) => issue.code);
const highRise = scenarioCards.find((card) => card.id === 'high-rise-building-fire');

/**
 * The scene detail — page B of the Scenario Center.
 *
 * TWO READING ENTRIES, ONE ISSUE SELECTOR. The three-stage step bar, the research sub-menu, the
 * planning side rail and the long explanation column are gone, so is the prominent 任务模板
 * entry. The reader picks an issue on the left and reads it on the right.
 *
 * EVERY CURRENT ITEM IS SHOWN. The sections render the whole linked set the source projection
 * returns — five information needs, four capabilities, whatever the issue actually carries. An
 * earlier revision showed three and hid the rest behind a toggle; that made a page about
 * completeness silently incomplete, and it also froze the visible count into the UI.
 */
export function ScenarioDetailView({
  data,
  location,
  onLocation,
  onLibrary,
}: {
  readonly data: ScenarioProjection;
  readonly location: DetailLocation;
  readonly onLocation: (next: DetailLocation) => void;
  readonly onLibrary: () => void;
}) {
  const [drawer, setDrawer] = useState<
    | { readonly kind: 'evidence' }
    | { readonly kind: 'capability'; readonly entry: ScenarioEntry }
    | null
  >(null);
  const [allEvidence, setAllEvidence] = useState(false);
  const information = useRef<HTMLElement>(null);
  const capabilities = useRef<HTMLElement>(null);
  /**
   * The element each drawer hands focus back to. A card opened with a pointer and one opened
   * with the keyboard are different controls, so the trigger records itself rather than the
   * drawer guessing from `document.activeElement`.
   */
  const opener = useRef<HTMLElement | null>(null);
  const openEvidence = (event: MouseEvent<HTMLButtonElement>) => {
    opener.current = event.currentTarget;
    setDrawer({ kind: 'evidence' });
  };
  const openCapability = (entry: ScenarioEntry, event: MouseEvent<HTMLButtonElement>) => {
    opener.current = event.currentTarget;
    setDrawer({ kind: 'capability', entry });
  };
  const issue = findIssue(location.issue) ?? findIssue(issueCodes[0] ?? '');
  const title = issue?.title ?? location.issue;
  /**
   * A drawer belongs to the issue it was opened from.
   *
   * A modal <dialog> does not stop the browser's Back button. Without this, pressing Back with a
   * drawer open re-rendered the page under a dialog that stayed open: the heading still named the
   * old capability while the body had already switched to "no per-issue explanation for this
   * capability", and focus was restored to a trigger that no longer existed. Closing on any
   * change of issue or tab keeps the drawer and the page describing the same thing.
   */
  useEffect(() => {
    setDrawer(null);
    setAllEvidence(false);
  }, [location.issue, location.tab]);
  useEffect(() => {
    if (location.tab !== 'issues' || !location.focus) return;
    const target = location.focus === 'information' ? information.current : capabilities.current;
    target?.scrollIntoView({ block: 'start' });
  }, [location.tab, location.focus, location.issue]);
  const linkedInformation = linkedEntries(data, location.issue, 'information');
  const linkedCapabilities = linkedEntries(data, location.issue, 'capability');
  return (
    <section className="scc-detail">
      <nav className="scc-breadcrumb" aria-label="面包屑">
        <button onClick={onLibrary}>场景中心</button>
        <span aria-hidden="true">›</span>
        <strong>高层建筑火灾</strong>
      </nav>
      <header className="scc-scene-head">
        <div className="scc-scene-art">
          <ScenarioCardArt id="high-rise-building-fire" name="高层建筑火灾" />
        </div>
        <div className="scc-scene-title">
          <h1>{highRise?.name ?? '高层建筑火灾'}</h1>
          <p>{highRise?.scope}</p>
        </div>
        <button className="scc-scene-back" onClick={onLibrary}>
          返回场景库 →
        </button>
      </header>
      <div
        className="scc-tab-bar"
        role="tablist"
        aria-label="场景详情"
        onKeyDown={handleScenarioTabsKeyDown}
      >
        {(
          [
            ['issues', '典型问题'],
            ['evidence', '依据与案例'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            role="tab"
            id={'scc-detail-tab-' + key}
            aria-selected={location.tab === key}
            aria-controls="scc-detail-panel"
            tabIndex={location.tab === key ? 0 : -1}
            onClick={() => onLocation({ ...location, tab: key, focus: null })}
          >
            {label}
          </button>
        ))}
      </div>
      <div id="scc-detail-panel" role="tabpanel" aria-labelledby={'scc-detail-tab-' + location.tab}>
        {location.tab === 'issues' ? (
          <div className="scc-issue-layout">
            <aside className="scc-issue-list" aria-label="典型问题">
              <h2>典型问题</h2>
              <ul>
                {issueContent.map((row) => (
                  <li key={row.code}>
                    <button
                      data-issue={row.code}
                      aria-current={row.code === location.issue ? 'true' : undefined}
                      onClick={() => onLocation({ issue: row.code, tab: 'issues', focus: null })}
                    >
                      <span>{row.title}</span>
                      <span aria-hidden="true">›</span>
                    </button>
                  </li>
                ))}
              </ul>
              <p>选择问题，查看相关准备内容。</p>
            </aside>
            <div className="scc-issue-main">
              <article className="scc-issue-intro">
                <div>
                  <h2>{title}</h2>
                  <p>{issue?.summary}</p>
                  <p className="scc-issue-impact">可能影响：{issue?.impact}</p>
                </div>
                <span className="scc-issue-icon" aria-hidden="true">
                  <Icon name="siren" />
                </span>
              </article>
              <section className="scc-read-section" ref={information} tabIndex={-1}>
                <div className="scc-heading-row">
                  <h2>需要掌握的信息</h2>
                  <button className="scc-text-button" onClick={openEvidence}>
                    查看相关依据 ↗
                  </button>
                </div>
                <p className="scc-section-lead">
                  为了理解当前问题，需要了解以下信息。它们是这类事故的通用需求，不是本次已掌握的情报。
                </p>
                <InformationRows
                  linked={linkedInformation}
                  issueCode={location.issue}
                  onEvidence={openEvidence}
                />
              </section>
              <section className="scc-read-section" ref={capabilities} tabIndex={-1}>
                <div className="scc-heading-row">
                  <h2>所需能力</h2>
                  <span className="scc-section-count">
                    {linkedCapabilities.status === 'AVAILABLE'
                      ? `${linkedCapabilities.entries.length} 项`
                      : ''}
                  </span>
                </div>
                <p className="scc-section-lead">
                  以下能力可以帮助了解或处理这类问题。它们是场景提出的需求，不代表本地已经具备。
                </p>
                <CapabilityCards
                  linked={linkedCapabilities}
                  issueCode={location.issue}
                  onCapability={openCapability}
                />
              </section>
              <ScenarioTechnicalReference issueCode={location.issue} />
              <footer className="scc-issue-footer">
                <span>参考相关文件与实际案例，理解这些内容的来源。</span>
                <button className="scc-primary" onClick={openEvidence}>
                  查看依据与案例
                </button>
              </footer>
            </div>
          </div>
        ) : (
          <EvidenceLibrary
            issueCode={location.issue}
            issueTitle={title}
            all={allEvidence}
            onAll={setAllEvidence}
          />
        )}
      </div>
      {drawer?.kind === 'evidence' && (
        <ScenarioEvidenceDrawer
          issueCode={location.issue}
          restoreFocusTo={opener.current}
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer?.kind === 'capability' && (
        <ScenarioCapabilityDrawer
          issueCode={location.issue}
          capability={drawer.entry}
          restoreFocusTo={opener.current}
          onClose={() => setDrawer(null)}
        />
      )}
    </section>
  );
}

function InformationRows({
  linked,
  issueCode,
  onEvidence,
}: {
  readonly linked: LinkedResult;
  readonly issueCode: string;
  readonly onEvidence: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  if (linked.status !== 'AVAILABLE')
    return (
      <div className="scc-notice" role="status" data-read-status={linked.status}>
        {linked.message}
      </div>
    );
  return (
    <ul className="scc-info-rows" data-linked-module="information">
      {linked.entries.map((entry) => {
        const editorial = entryStudyRelation(issueCode, 'information', entry.code);
        return (
          <li className="scc-info-row" key={entry.code} data-source-code={entry.code}>
            <div className="scc-info-row-main">
              <strong>{entryLabel(entry, 'information')}</strong>
              {editorial ? (
                <>
                  <p>{editorial.why}</p>
                  <p className="scc-info-expect">需要获取：{editorial.expectation}</p>
                  <p>通常来源：{informationAcquisition[entry.code] ?? '资料来源待核实'}</p>
                </>
              ) : (
                <p data-read-status="MISSING_REFERENCE">
                  该条目的逐项说明尚未提供。不展示通用说明替代。
                </p>
              )}
            </div>
            <button className="scc-text-button" onClick={onEvidence}>
              查看依据 ↗
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function CapabilityCards({
  linked,
  issueCode,
  onCapability,
}: {
  readonly linked: LinkedResult;
  readonly issueCode: string;
  readonly onCapability: (entry: ScenarioEntry, event: MouseEvent<HTMLButtonElement>) => void;
}) {
  if (linked.status !== 'AVAILABLE')
    return (
      <div className="scc-notice" role="status" data-read-status={linked.status}>
        {linked.message}
      </div>
    );
  return (
    <ul className="scc-cap-grid" data-linked-module="capability">
      {linked.entries.map((entry) => {
        const reading = highriseCapabilityReading(
          issueCode,
          entry.code,
          entryLabel(entry, 'capability'),
        );
        return (
          <li className="scc-cap-card" key={entry.code} data-source-code={entry.code}>
            <ScenarioCapabilityReadingCard
              reading={reading}
              onOpen={(event) => onCapability(entry, event)}
            />
          </li>
        );
      })}
    </ul>
  );
}

/**
 * 依据与案例: grouped by what the source IS, filtered to the issue the reader came from.
 *
 * The filter defaults to the current issue because that is the question the reader asked; the
 * toggle shows the whole scenario. Nothing is hidden silently — the caption says which view is
 * in effect and how many sources each view holds.
 */
export function EvidenceLibrary({
  issueCode,
  issueTitle,
  all,
  onAll,
}: {
  readonly issueCode: string;
  readonly issueTitle: string;
  readonly all: boolean;
  readonly onAll: (all: boolean) => void;
}) {
  const documents = officialDocuments.filter((row) => all || row.issues.includes(issueCode));
  const cases = officialCases.filter((row) => all || row.issues.includes(issueCode));
  const groups = evidenceGroupOrder
    .map((group): { group: EvidenceGroup; rows: readonly (OfficialDocument | OfficialCase)[] } => ({
      group,
      rows: [
        ...documents.filter((row) => evidenceGroupOf(row.kind) === group),
        ...cases.filter((row) => evidenceGroupOf(row.kind) === group),
      ],
    }))
    .filter((row) => row.rows.length > 0);
  const total = officialDocuments.length + officialCases.length;
  const shown = documents.length + cases.length;
  return (
    <section className="scc-evidence-page">
      <div className="scc-heading-row">
        <h2>依据与案例</h2>
        <div className="scc-evidence-switch" role="group" aria-label="资料范围">
          <button aria-pressed={!all} onClick={() => onAll(false)}>
            与本问题相关
          </button>
          <button aria-pressed={all} onClick={() => onAll(true)}>
            本场景全部资料（{total}）
          </button>
        </div>
      </div>
      <p className="scc-section-lead">
        {all
          ? `本场景已登记的全部 ${total} 份资料。`
          : `当前显示与“${issueTitle}”相关的 ${shown} 份资料；可切换到本场景全部资料。`}
        先读摘要，再按需要查阅官方原文。外部链接在新标签页打开。
      </p>
      {groups.length === 0 ? (
        <div className="scc-notice" role="status" data-read-status="EMPTY_FILTER_RESULT">
          该问题尚无已登记的直接依据。可切换到本场景全部资料查看其他来源。
        </div>
      ) : (
        groups.map(({ group, rows }) => (
          <section className="scc-evidence-group" key={group}>
            <h3>{group}</h3>
            <div className="scc-evidence-grid">
              {rows.map((row) =>
                'date' in row ? (
                  <CaseCard key={row.id} incident={row} />
                ) : (
                  <DocumentCard key={row.id} document={row} />
                ),
              )}
            </div>
          </section>
        ))
      )}
    </section>
  );
}

function DocumentCard({ document }: { readonly document: OfficialDocument }) {
  return (
    <article className="scc-evidence-card" data-evidence-id={document.id}>
      <span className="scc-chip">
        {document.kind} · {document.publisher}
      </span>
      <h4>{document.name}</h4>
      <p>{document.note}</p>
      <p className="scc-evidence-use">{document.use}</p>
      <p className="scc-evidence-locator">关联问题：{relatedIssueTitles(document.issues)}</p>
      <a href={document.url} target="_blank" rel="noopener noreferrer">
        {document.linkLabel}
        <span aria-hidden="true"> ↗</span>
      </a>
    </article>
  );
}

function CaseCard({ incident }: { readonly incident: OfficialCase }) {
  return (
    <article className="scc-evidence-card" data-evidence-id={incident.id}>
      <span className="scc-chip">
        {incident.kind} · 发生于 {incident.date}
      </span>
      <h4>{incident.name}</h4>
      <p>{incident.summary}</p>
      <p className="scc-evidence-use">{incident.use}</p>
      <p className="scc-evidence-classification">
        资料性质：{incident.classification} · {incident.publisher}
      </p>
      <p className="scc-evidence-locator">关联问题：{relatedIssueTitles(incident.issues)}</p>
      <a href={incident.url} target="_blank" rel="noopener noreferrer">
        {incident.linkLabel}
        <span aria-hidden="true"> ↗</span>
      </a>
    </article>
  );
}

function relatedIssueTitles(codes: readonly string[]): string {
  return codes.map((code) => issueBusinessTitles[code] ?? code).join('、');
}
